import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { CustomBackdrop, Header, NavHeader, Page, Tabs } from '../../../../components';
import { useRouter } from '../../../../hooks/router/use-router';
import { ROUTES } from '../../../../routes/routes';
import { pathnameWithParameters } from '../../../../utils/location';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import ConfigurationDetails from './configuration-details';
import DeviceBindings from './device-bindings';
import DeviceChannels from './device-channels';
import DeviceInfo from './device-info';
import DeviceInputs from './device-inputs';
import { useDeviceSettings } from './hooks/use-device-settings';
import { useGetDeviceDetails } from './hooks/use-get-device-details';
import './index.scss';

const DeviceSettings: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation('channel-list');
  const { isBackAvailable, handleReplaceRedirect } = useRouter();
  const { deviceId } = useParams<{ deviceId: string; channelId: string }>();
  const { device } = useDevicesAndChannelsContext();
  const { inputs, bindingsEnabled, rssiSupported, tabs } = useDeviceSettings();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const DeviceId = deviceId ?? '';
  const { loading } = useGetDeviceDetails(DeviceId);

  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) setActiveTabIndex(parseInt(tab));
  }, []);

  const handleTabChange = (tabIndex: number) => {
    navigate(pathnameWithParameters(ROUTES.DeviceSettings(DeviceId), search, { key: 'tab', value: tabIndex }), {
      replace: true,
    });
    setActiveTabIndex(tabIndex);
  };

  const renderCurrentForm = () => {
    switch (activeTabIndex) {
      case 1:
        return (
          <>
            <Header subheader title={t('channels')} />
            <DeviceChannels />
          </>
        );
      case 2:
        return <DeviceInputs inputs={inputs} bindingsEnabled={bindingsEnabled} />;
      case 3:
        return <DeviceBindings />;
      default:
        return <ConfigurationDetails rssiSupported={rssiSupported} />;
    }
  };

  return (
    <Page
      isStickyHeader
      className="driver-settings__page"
      header={
        <>
          <NavHeader {...(!isBackAvailable ? { onClick: () => handleReplaceRedirect(ROUTES.Root()) } : {})} />
          <Header>
            <div className="driver-settings__header">{device && <DeviceInfo device={device} />}</div>
          </Header>
          <Tabs
            tabList={tabs}
            activeTabIndex={activeTabIndex}
            setActiveTabIndex={handleTabChange}
            tabsType="pills"
            isDivider
          />
        </>
      }
    >
      {device && <div className="p-b-32">{renderCurrentForm()}</div>}
      <CustomBackdrop loading={loading} />
    </Page>
  );
};

export default DeviceSettings;
